import LZString from 'lz-string';
import { trackEvent } from '../plugins/analytics';
import feedChunksConfig from '../config/feedChunks.config';

/**
 * Utilidades para compartir cards de noticias mediante URLs comprimidas
 */

/**
 * Codifica los datos de un item para compartir
 * @param {Object} item - Item completo del feed
 * @returns {String} - Hash comprimido para usar en URL
 */
export function encodeShareData(item) {
  try {
    // Crear objeto con keys cortas y LÍMITES ESTRICTOS para prevenir URLs muy largas
    const shareData = {
      t: truncateText(item.title, 200),        // title (MAX: 200 chars)
      l: truncateText(item.link, 400),         // link (MAX: 400 chars, por si acaso)
      d: truncateText(item.description, 250),  // description (reducido de 300)
      p: item.pubDate || null,                 // pubDate
      sc: truncateText(item.sourceCategory, 50), // sourceCategory (MAX: 50)
      st: truncateText(item.sourceType, 20),   // sourceType (MAX: 20)
      stl: truncateText(item.sourceTitle, 50), // sourceTitle (MAX: 50)
      a: truncateText(item.author, 50),        // author (MAX: 50, crítico!)
      i: truncateText(item.image, 300),        // image (MAX: 300 chars)
      c: truncateText(item.content, 300),      // content (reducido de 500)
      sv: item.isShortVideo || false           // isShortVideo (boolean)
    };

    // Convertir a JSON y comprimir
    const jsonString = JSON.stringify(shareData);
    const compressed = LZString.compressToEncodedURIComponent(jsonString);

    // VALIDACIÓN: Verificar que la URL final no exceda límites
    const urlLength = compressed.length + 38; // +38 para base URL

    if (urlLength > 2000) {
      console.warn(`⚠️ URL muy larga (${urlLength} chars). Truncando más agresivamente...`);

      // Fallback: truncar aún más
      shareData.d = truncateText(item.description, 150);
      shareData.c = truncateText(item.content, 150);
      shareData.a = truncateText(item.author, 30);

      const compressedFallback = LZString.compressToEncodedURIComponent(JSON.stringify(shareData));
      const fallbackLength = compressedFallback.length + 38;

      console.log(`✅ URL truncada a ${fallbackLength} chars`);
      return compressedFallback;
    }

    return compressed;
  } catch (error) {
    console.error('Error encoding share data:', error);
    return null;
  }
}

/**
 * Decodifica los datos compartidos desde el hash de URL
 * @param {String} shareHash - Hash comprimido de la URL
 * @returns {Object|null} - Objeto con datos del item o null si hay error
 */
export function decodeShareData(shareHash) {
  try {
    // Descomprimir y parsear JSON
    const decompressed = LZString.decompressFromEncodedURIComponent(shareHash);

    if (!decompressed) {
      console.error('Failed to decompress share data');
      return null;
    }

    const shareData = JSON.parse(decompressed);

    // Reconstruir objeto con keys completas
    const item = {
      title: shareData.t || '',
      link: shareData.l || '',
      description: shareData.d || '',
      pubDate: shareData.p ? new Date(shareData.p) : null,
      sourceCategory: shareData.sc || '',
      sourceType: shareData.st || '',
      sourceTitle: shareData.stl || '',
      author: shareData.a || 'Desconocido',
      image: shareData.i || null,
      content: shareData.c || '',
      isShortVideo: shareData.sv || false,
      // ID generado del link para compatibilidad
      id: shareData.l || `shared-${Date.now()}`
    };

    return item;
  } catch (error) {
    console.error('Error decoding share data:', error);
    return null;
  }
}

/**
 * Genera la URL completa para compartir un item
 * @param {Object} item - Item del feed
 * @param {String} baseUrl - URL base (default: window.location.origin)
 * @returns {String} - URL completa para compartir
 */
export function generateShareUrl(item, baseUrl = null) {
  const encoded = encodeShareData(item);

  if (!encoded) {
    console.error('Failed to generate share URL');
    return null;
  }

  const origin = baseUrl || window.location.origin;
  return `${origin}/news/shared/${encoded}`;
}

/**
 * Id corto de un item para /news/s/:id: los 12 primeros hex de su UUID (el que
 * trae cada item de los feed-chunks). Debe coincidir con las claves de
 * share-index.json del proyecto de feeds.
 * @param {String} uuid - Id del item
 * @returns {String|null} - Id corto o null si no es un UUID
 */
export function shortShareId(uuid) {
  const hex = String(uuid || '').replace(/-/g, '').toLowerCase();
  return /^[0-9a-f]{32}$/.test(hex) ? hex.slice(0, 12) : null;
}

/**
 * URL para compartir: corta si el item viene de los feed-chunks; si no (no
 * tiene UUID), el enlace largo con el item comprimido.
 * @param {Object} item - Item del feed
 * @returns {String} - URL para compartir
 */
function shareUrlFor(item) {
  const id = shortShareId(item.id);
  return id ? `${window.location.origin}/news/s/${id}` : generateShareUrl(item);
}

/**
 * Busca un item compartido: share-index.json dice en qué chunk está y de ese
 * chunk se toma el item. null si ya salió de los chunks (solo cubren los
 * últimos 2 meses, y menos en las categorías con tope de chunks).
 * @param {String} id - Id corto del enlace
 * @returns {Promise<Object|null>} - Item o null si no se encuentra
 */
export async function fetchSharedItem(id) {
  if (!/^[0-9a-f]{12}$/.test(id)) return null;
  try {
    const index = await (await fetch(`${feedChunksConfig.baseUrl}share-index.json`)).json();
    const chunk = index[id];
    if (!chunk) return null;
    const items = await (await fetch(`${feedChunksConfig.baseUrl}${chunk}.json`)).json();
    const item = items.find(i => shortShareId(i.id) === id);
    if (!item) return null;
    return {
      ...item,
      pubDate: item.pubDate ? new Date(item.pubDate) : null,
      author: item.author || 'Desconocido',
      image: item.image || null,
    };
  } catch (error) {
    console.error('Error fetching shared item:', error);
    return null;
  }
}

/**
 * Copia la URL de compartir al clipboard
 * @param {Object} item - Item del feed
 * @returns {Promise<Boolean>} - true si se copió correctamente
 */
export async function copyShareUrl(item) {
  try {
    trackEvent('share', { method: 'copy_link', content_type: item.sourceType, item_id: item.id || item.link });

    const shareUrl = shareUrlFor(item);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } else {
      // Fallback para navegadores antiguos
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    }
  } catch (error) {
    console.error('Error copying share URL:', error);
    return false;
  }
}

/**
 * Trunca texto a un máximo de caracteres
 * @param {String} text - Texto a truncar
 * @param {Number} maxLength - Longitud máxima
 * @returns {String} - Texto truncado
 */
function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) {
    return text || '';
  }
  return text.substring(0, maxLength) + '...';
}

/**
 * Genera meta tags para Open Graph y Twitter Card
 * @param {Object} item - Item del feed
 * @returns {Object} - Objeto con meta tags
 */
export function generateMetaTags(item) {
  const metaTags = {
    // Open Graph
    'og:title': item.title || 'Devlusaja | Noticias IA',
    'og:description': truncateText(item.description, 200) || 'Hub de noticias y contenido sobre inteligencia artificial.',
    'og:type': 'article',
    'og:url': window.location.href,

    // Twitter Card
    'twitter:card': 'summary_large_image',
    'twitter:title': item.title || 'Devlusaja | Noticias IA',
    'twitter:description': truncateText(item.description, 200) || 'Hub de noticias y contenido sobre inteligencia artificial.',

    // Meta description
    'description': truncateText(item.description, 160) || 'Hub de noticias y contenido sobre inteligencia artificial.'
  };

  // Agregar imagen si existe
  if (item.image) {
    metaTags['og:image'] = item.image;
    metaTags['twitter:image'] = item.image;
  } else {
    // Imagen por defecto
    metaTags['og:image'] = `${window.location.origin}/assets/img/preview-newsai.png`;
    metaTags['twitter:image'] = `${window.location.origin}/assets/img/preview-newsai.png`;
  }

  // Agregar fecha de publicación si existe
  if (item.pubDate) {
    metaTags['article:published_time'] = new Date(item.pubDate).toISOString();
  }

  // Agregar autor si existe
  if (item.author && item.author !== 'Desconocido') {
    metaTags['article:author'] = item.author;
  }

  return metaTags;
}

/**
 * Aplica meta tags al documento
 * @param {Object} metaTags - Objeto con meta tags
 */
export function applyMetaTags(metaTags) {
  Object.entries(metaTags).forEach(([name, content]) => {
    setMetaTag(name, content);
  });
}

/**
 * Establece o actualiza un meta tag
 * @param {String} name - Nombre o property del meta tag
 * @param {String} content - Contenido del meta tag
 */
function setMetaTag(name, content) {
  let tag =
    document.querySelector(`meta[name="${name}"]`) ||
    document.querySelector(`meta[property="${name}"]`);

  if (tag) {
    tag.setAttribute('content', content);
  } else {
    tag = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      tag.setAttribute('property', name);
    } else {
      tag.setAttribute('name', name);
    }
    tag.setAttribute('content', content);
    document.head.appendChild(tag);
  }
}
